import { settingAction } from "./setting-modal/setting.action";
import { SharedActions } from "./shared/shared.actions";
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { environment } from "../environments/environment";
import { DOCUMENT, registerLocaleData } from "@angular/common";
import { DynamicCaptionService } from "./shared/services/dynamic-caption.service";
import { ApplicationUserActions } from "./pb-screen/application.user.actions";
import { HttpClientService } from "./shared/services/httpclient";
import { SharedService } from "./shared/services/shared.service";
import { PlanningConflictsService } from "./shared/services/planning-conflicts.service";
import { ApplicationUser, View } from "./pb-screen/models/application.user.interface";
import { select } from "@angular-redux/store";
import { PbScreenService } from "./pb-screen/services/pb-screen.service";
import { PlanningBoardService } from "./planningBoard/services/planningBoard.service";
import { PlanningBoardActions } from "./planningBoard/planningBoard.action";
import { UserSettingsService } from "./pb-screen/services/usersettings.service";
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from "@angular/router";
import { LangChangeEvent, TranslateService } from "@ngx-translate/core";
import { SimpleModalService } from "ngx-simple-modal";
import { VersionComponent } from "./shared/components/modals/version-modal/version-modal.component";
import { MsalBroadcastService, MsalService } from "./msal";
import { EventMessage, EventType, IPublicClientApplication, PublicClientApplication } from "@azure/msal-browser";
import { MsalGuardConfiguration } from "./msal/msal.guard.config";
import { MSAL_GUARD_CONFIG } from "./msal/constants";
import { filter, takeUntil } from "rxjs/operators";
import { ApplicationInsightsLogger } from "./shared/services/app-logging/applications-insights-logger";
import { SupportedLanguages } from "../resources/supported-languages.enum";
import { DetermineElementVisibilityService } from "./shared/services/determine-element-visibility.service";
import localeNl from "@angular/common/locales/nl";
import localeEn from "@angular/common/locales/en";
import localeDa from "@angular/common/locales/da";
import { MergeTranslationService } from "./shared/services/merge-translation.service";
import { UsersAuthorizationService } from "./users-authorizations/user-authorization.service";
import FontAwesome from '@fortawesome/fontawesome-free';
import { PlanningBoardFacade } from "./planningBoard/services/planning-board.facade";
import { OptimisticPlanningService } from "./optimistic-planning/optimistic-planning.service";


const locales = {
  "nl": localeNl,
  "en": localeEn,
  "da": localeDa,
};

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  moduleId: module.id,
})

export class AppComponent implements OnInit , OnDestroy {
  public connectionState$: Observable<string>;
  private user: ApplicationUser;
  public environment = environment.envName;
  public userId: string = "";
  public destination: string;
  weekPlanningUrl: string;
  public showSettingsIcon: boolean = false;
  public showDepotInIcon: boolean = true;
  public showDepotOutIcon: boolean = true;
  public showWaitingRoom: boolean = false ;
  public showDeliveryIcon: boolean = true;
  public showCollectionIcon: boolean = true;
  public showStackerScreen: boolean = false;
  public showCopySettingsModal: boolean = false;
  public showAuthorization: boolean = true;
  public showFooter: boolean = true;

  public buisnessCentralUrl: string = environment.buisnessCentralUrl;
  public isLocalhost: boolean = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  public bcEnvironment: string = this.extractBcEnvironment(environment.buisnessCentralUrl);
  private readonly _destroying$ = new Subject<void>();
  public views: View[];
  public iconType: string;
  public isIframe = false;
  public loggedIn = false;
  @select("applicationUser") applicationUser$: Observable<ApplicationUser>;
  @select("azureGroups") azureGroups$: Observable<any[]>;
  public screens: string[] = [];
  hideSidebar: boolean = false;

  constructor(
    @Inject(DOCUMENT) private document: any,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private readonly dynamicCaptionService: DynamicCaptionService,
    private readonly httpClientService: HttpClientService,
    private readonly sharedService: SharedService,
    private readonly applicationUserActions: ApplicationUserActions,
    private readonly settingActions: settingAction,
    private readonly shardActions: SharedActions,
    private readonly planningConflictsService: PlanningConflictsService,
    private pbScreenService: PbScreenService,
    private userSettingsService: UserSettingsService,
    private planningBoardService: PlanningBoardService,
    private planningBoardAction: PlanningBoardActions,
    private translate: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private simpleModalService: SimpleModalService,
    private applicationInsightsLogger: ApplicationInsightsLogger,
    private determineElementVisibilityService: DetermineElementVisibilityService,
    private mergeTranslationService: MergeTranslationService,
    private userAuthorizationService: UsersAuthorizationService,
    private planningBoardFacade: PlanningBoardFacade,
    private readonly optimisticPlanningService: OptimisticPlanningService  // injected for early init — mutates environment before any component reads the flag
  ) {
  }

  public ngOnInit() {
    this.setShowFooter();

    const supportedLanguages = this.getSupportedLanguages();
    this.translate.addLangs(supportedLanguages);

    // Don't have the user yet so initial is ENGLISH
    this.translate.setDefaultLang(SupportedLanguages.English);

    const browserLanguage = this.translate.getBrowserLang();

    this.onLanguageChange();

    this.applicationUser$.pipe(
      filter(applicationUser => applicationUser != null),
      takeUntil(this._destroying$)
    ).subscribe((applicationUser) => {
      this.user = applicationUser;
      this.setLanguageToUse(applicationUser.planningToolLanguage, supportedLanguages, browserLanguage);

      this.mergeTranslationService.loadBargeAndCaptainLabels(this.user.enableBargeAndCaptainLabels);

      this.setWeekPlanningUrl(applicationUser);

      // Apply the saved "Optimistic UI" settings BEFORE anything reads the flags, so OFF = exact
      // standard/main behavior and ON = optimistic. The board needs both the tenant-wide switch
      // and the user's own preference; the effective result is mirrored into
      // environment.enableOptimisticPlanning (the board/PB-screen code reads that flag).
      this.optimisticPlanningService.setDepotEnabled(!!applicationUser.enableOptimisticUi);
      this.optimisticPlanningService.setBoardGeneralEnabled(!!applicationUser.enableOptimisticUiPlanningBoard);
      this.optimisticPlanningService.setBoardUserEnabled(applicationUser.enableOptimisticUiPlanningBoardUser !== false);

      // Initialize SignalR for optimistic UI (after user data is loaded)
      this.initializeOptimisticSignalR(applicationUser);
    });

    sessionStorage.removeItem("tabID");



    this.isIframe = window !== window.parent && !window.opener;

    this.checkAccount();


    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS),
        takeUntil(this._destroying$)
      ).subscribe((result) => {
          this.checkAccount();
          if (result.eventType == "msal:loginSuccess" ) {
          this.SetApplicationDefaults() ;
          }
      });

    this.login();
  }

  private onLanguageChange() {
    this.translate.onLangChange
      .pipe(takeUntil(this._destroying$), filter(_ => !!this.user))
      .subscribe((event: LangChangeEvent) => this.mergeTranslationService.loadBargeAndCaptainLabels(this.user.enableBargeAndCaptainLabels));
  }

  SetApplicationDefaults() {
    const account = this.authService.instance.getAllAccounts()[0];

    if (!this.authService.instance.getActiveAccount()) {
        this.authService.instance.setActiveAccount(account);
    }



    this.iconType = environment.iconType;
    if (this.loggedIn) {
      this.getUserData();
    }
    this.azureGroups$.subscribe((groups) => {
      if (groups) {
        this.showSettingsIcon = groups.map(x => x.name.toLowerCase()).indexOf(environment.adminGroup.toLowerCase()) !== -1;
        this.showAuthorization = environment.enableAuthorizationModule && groups.map(x => x.name.toLowerCase()).indexOf(environment.adminGroup.toLowerCase()) !== -1;
      }
    });
    this.subscribeToViewParam();
    this.subscribeToReroute();
    this.showDepotInIcon = environment.depotInIcon != null ? environment.depotInIcon : true;
    this.showDepotOutIcon = environment.depotOutIcon != null ? environment.depotOutIcon : true;
    this.showDeliveryIcon = environment.deliveryIcon != null ? environment.deliveryIcon : true;
    this.showWaitingRoom = environment.showWaitingRoom != null ? environment.showWaitingRoom : true;
    this.showCollectionIcon = environment.collectionIcon != null ? environment.collectionIcon : true;
    this.showStackerScreen = environment.stackerIcon != null ? environment.stackerIcon : true;

    this.applicationUser$.subscribe((data) => {
      if (data) {
        if(environment.enableAuthorizationModule)
          this.screens = data.userAuthorization.screenAuthorizations.filter(a => a.authorized).map(a => a.screenName);

        this.shardActions.setActivePlanningConflicts(this.userAuthorizationService.getAuthorizedPlanningGroups(data).join(","));

      }
    })

}

  subscribeToReroute() {
    this.router.events.pipe(filter(event => event instanceof NavigationStart))
      .subscribe(el => {
        this.shardActions.resetState();

      });

    this.router.events.pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(el => {
        const route = this.router.routerState.snapshot.root.firstChild;
        this.hideSidebar = route?.data?.hideSidebar ;
      });
  }

  showVersionModal() {
    this.simpleModalService.addModal(VersionComponent, {
      title: "VERSION_MODAL.TITLE",
      message: "VERSION_MODAL.MESSAGE"
    }, { closeOnEscape: false, closeOnClickOutside: false }).subscribe(() => { });
  }

  getUserData() {
    const account = this.authService.instance.getAllAccounts()[0];

    this.httpClientService.getToken().subscribe((token) => {
      if (account && account.localAccountId) {
        const userId = account.localAccountId;

        localStorage.setItem("userId", userId);
        localStorage.setItem("userName", account.name + "(" + account.username + ")");
        sessionStorage.setItem("userName", account.name + "(" + account.username + ")");


        this.shardActions.storeUserAzureGroups(userId);
        this.applicationUserActions.getAppUserAndDefaultPreferences(["defaultPbUserSettings", "defaultToUserSettings",
          "defaultOrderLineUserSettings", "defaultGridIcon", "defaultDateFormat", "defaultPlanDate",
          "defaultAgendaDateRange", "defaultTruckAgendaDateRange", "defaultAgendaPlanningGroup", "defaultAgendaPlanningZone"
          , "depotOutSelectedColumns", "depotInSelectedColumns", "stackerSelectedColumns", "depotOutSavedAdvancedFilters"
          , "depotInSavedAdvancedFilters", "quickViews", "planningBlocksPriority", "planningGroups", "siteNumber", "depotInPreferences",
          "depotOutPreferences", "depotOutGridIcons", "depotInGridIcons", "depotQuickViews", "showAbsentDriversPlan", "enableContainerSwap", "buttonsVisibility", "checkDateCompatibility", "weekPlanningPreferences", "tmsActionCodeColors", "weekPlanningCapacitySlotColumnSelectionSettings",
          "truckFilters", "usePopupScreenForEditingOrderlines", "enableBargeAndCaptainLabels", "suggestPlanDateOption", "serverSideFiltering"
          ,"userAuthorization"
        ], userId).subscribe(
              (user) => {
                this.dynamicCaptionService.getDynamicCaptions().subscribe();
                if (user.generalSetting.useContainerStatuses) {
                  this.shardActions.getContainerStatuses();
                }
              });
        this.settingActions.getDefaultGlobalPreference(["globalPrintSetting",
          "timeline", "changeSequence", "collapsedPbs"]).subscribe(userSettings => {
            let result = userSettings.find(el => el.setting.name == "timeline");
          if (result.value) {
              localStorage.setItem("timeLine" , result.value);
            } else {
              localStorage.setItem("timeLine" , "");
            }

            result = userSettings.find(el => el.setting.name == "collapsedPbs");
          if (result.value) {
              localStorage.setItem("collapsedPbs" , result.value);
            } else {
              localStorage.setItem("collapsedPbs" , "");
            }

          });
        // this.shardActions.setDefaultPlanningZone(userId);
        this.shardActions.setTMSPlanningSetup();
        this.shardActions.storeIgnoreConflictReasons();
        this.applicationUserActions.setAzureUsers();
        this.shardActions.storePlanningZone();
        this.shardActions.getPlanningGroups();

      }
      this.userSettingsService.getViews().subscribe(views => this.views = views);

    });
  }

  private subscribeToViewParam() {
    const account = this.authService.getAccount();

    this.route.queryParamMap.subscribe(queryParams => {
      // refresh user only for pb-screen -> view parameter might have changed
      if (account && account.localAccountId && this.router.url.includes("/pb-screen")) {
        this.applicationUserActions.setApplicationUser(account.localAccountId).subscribe();
      }
    });
  }

  checkoutAccount() {
    this.loggedIn = !!this.authService.getAccount();
  }

  login() {
    this.checkAccount();
    if (this.loggedIn) {
      this.SetApplicationDefaults();
      return;
    }


    this.authService.loginPopup().subscribe (() => {
      this.checkAccount();
      if (this.loggedIn) {
        this.SetApplicationDefaults();
      }
    });

    this.SetApplicationDefaults();
  }

  logOut() {
    this.authService.logout();
  }

  test() {
    this.sharedService.getTMSPlanningSetup().subscribe((data) => {
      alert("ok");
    });
  }


  accessRoute(route) {
    this.router.navigate([route]);
  }

  openCopySettings() {
    this.applicationUserActions.setAzureUsers();
    this.showCopySettingsModal = true;
  }

  toogleMenu(ev, open) {
    const element = ev.target as Element;
    if (open) {
      element.classList.add("open");
    } else {
      element.classList.remove("open");
    }
  }

  onRightClick(page: string, queryParams: string = "") {
    window.open(environment.redirectUrl + "/" + page + queryParams);

    return false;
  }

  onClick(e, page: string, queryParams: string = "") {
    e.preventDefault();
    if (e.which == 2) {
      window.open(environment.redirectUrl + "/" + page + queryParams);
    }
  }

  checkAccount() {
    this.loggedIn = this.authService.instance.getAllAccounts().length > 0;
  }

  changeRoute() {
    this.planningBoardAction.setLoading(true);
  }

  changeWeekPlanningRoute() {
    this.planningBoardAction.setLoading(true);

    this.router.navigate([`/${this.weekPlanningUrl}`]).then();
  }

  hideCopySettingsModal() {
    this.showCopySettingsModal = false;
  }

  ngOnDestroy(): void {
    this._destroying$.next(null);
    this._destroying$.complete();
  }

  private setLanguageToUse(usersPlanningToolLanguage: string, supportedLanguages: string[], browserLanguage: string) {
    if (usersPlanningToolLanguage) {
      this.translate.use(usersPlanningToolLanguage);
    } else if (new RegExp(supportedLanguages.join("|")).test(browserLanguage)) {
      this.translate.use(browserLanguage);
    } else {
      this.translate.use(SupportedLanguages.English);
    }

    // So the date pipe can use the correct locale
    this.setAngularLocale(this.translate.currentLang);
  }

  private getSupportedLanguages() {
    const supportedLanguages: string[] = [];

    for (const language in SupportedLanguages) {
      if (Object.prototype.hasOwnProperty.call(SupportedLanguages, language)) {
        supportedLanguages.push(SupportedLanguages[language]);
      }
    }

    return supportedLanguages;
  }

  private setShowFooter() {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(_ => {
      this.showFooter = this.determineElementVisibilityService.footerVisibility(this.router.routerState.snapshot.url.split("?")[0]);
    });
  }

  private setWeekPlanningUrl(applicationUser: ApplicationUser) {
    if (applicationUser.weekPlanningPreferences?.tabletMode) {
      this.weekPlanningUrl = "week-planning";
    } else if (!applicationUser.weekPlanningPreferences?.tabletMode) {
      this.weekPlanningUrl = "multi-day-compact";
    }
  }

  private setAngularLocale(lang: string) {
    if (locales[lang]) {
      registerLocaleData(locales[lang]);
    }
  }

  public checkScreen(name) {
    if(! environment.enableAuthorizationModule)
    {
      return true;
    }
    if (this.screens && this.screens.length > 0) {
      let index = this.screens.indexOf(name);
      if (index == -1) return false;
      else return true;

    } else return false;

  }

  /**
   * Initialize SignalR for optimistic UI after user login
   * This enables real-time planning confirmations/failures
   */
  private initializeOptimisticSignalR(applicationUser: ApplicationUser): void {
    // Only initialize if optimistic planning is enabled in either domain (board or depot)
    if (!this.optimisticPlanningService.anyEnabled) {
      return;
    }

    try {
      const account = this.authService.getAccount();
      if (!account) {
        return;
      }

      const userId = account.localAccountId || account.username;
      const planningGroups = applicationUser.defaultPlanGroups || [];

      // Initialize SignalR via the facade
      this.planningBoardFacade.initializeSignalR(userId, planningGroups);

      console.log('[Optimistic] SignalR initialized for user:', userId);
    } catch (error) {
      console.error('[Optimistic] Failed to initialize SignalR:', error);
    }
  }
  private extractBcEnvironment(url: string): string {
    if (!url) return '';
    try {
      const parts = url.replace(/\/+$/, '').split('/');
      return parts[parts.length - 1] || '';
    } catch {
      return '';
    }
  }

  /** Badge style and icon for BC environment indicator (localhost only). */
  get bcBadgeStyle(): { background: string; icon: string } {
    const env = (this.bcEnvironment || '').toUpperCase();
    if (env.includes('LIVE')) {
      return { background: '#c5221f', icon: 'fa fa-exclamation-circle' };  // Red - production
    }
    if (env.includes('UAT')) {
      return { background: '#e65100', icon: 'fa fa-exclamation-triangle' }; // Orange - UAT
    }
    return { background: '#1a73e8', icon: 'fa fa-code' };  // Blue - DEV/TEST
  }
}
